import React, { useMemo } from "react";

import {
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AppsIcon from "@mui/icons-material/Apps";
import TableRowsIcon from "@mui/icons-material/TableRows";

import MainLayout from "../../layouts/main-layout";
import {
  useListOrganisations,
  useListUsers,
  useGetCurrentUser,
} from "../../store";
import { User, UserType } from "../../types";
import RoleChip, { type RoleChipValue } from "../../components/chips/role-chip";
import { createTeamDictionary } from "../../utils/create-dictionary";
import UsersCardsView from "./components/users-cards-view";
import UsersTableView from "./components/users-table-view";
import InviteUserModal from "./components/invite-user-modal";
import {
  canInviteUsers,
  canViewRestrictedUsers,
} from "../../utils/user-permissions";

const USER_ROLE_FILTER_OPTIONS: RoleChipValue[] = [
  "Developer Admin",
  "Client Admin",
  "Team Admin",
  "Member",
  "Guest",
  "Past Member",
  "Blocked",
];

const ROLE_CHIP_OPACITY = 0.42;

type RoleFilterMode = "all" | "none" | "partial";

const getUserRoleChipValue = (user: User): RoleChipValue => {
  if (user.userType === UserType.DeveloperAdmin) return "Developer Admin";
  if (user.userType === UserType.SysAdmin) return "Client Admin";
  if (user.userType === UserType.OrgAdmin) return "Team Admin";
  if (user.userType === UserType.Guest) return "Guest";
  if (user.userType === UserType.Blocked) return "Blocked";
  if (user.userType === UserType.Inactive) return "Past Member";

  return "Member";
};

const UsersPage: React.FC = () => {
  const { data: usersList = [], isLoading: isUsersLoading } = useListUsers();
  const { data: currentUser } = useGetCurrentUser();
  const { data: teams = [] } = useListOrganisations();

  const isCurrentUserDeveloperAdmin =
    currentUser?.userType === UserType.DeveloperAdmin;
  const canInviteUser = canInviteUsers(currentUser);
  const canViewRestrictedUserTypes = canViewRestrictedUsers(currentUser);

  const roleFilterOptions = useMemo(() => {
    const baseOptions = canViewRestrictedUserTypes
      ? USER_ROLE_FILTER_OPTIONS
      : USER_ROLE_FILTER_OPTIONS.filter(
          (option) => option !== "Past Member" && option !== "Blocked",
        );

    if (isCurrentUserDeveloperAdmin) {
      return baseOptions;
    }

    return baseOptions.filter((option) => option !== "Developer Admin");
  }, [canViewRestrictedUserTypes, isCurrentUserDeveloperAdmin]);

  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [roleFilter, setRoleFilter] = React.useState<RoleChipValue[]>([
    ...USER_ROLE_FILTER_OPTIONS,
  ]);
  const [roleFilterMode, setRoleFilterMode] =
    React.useState<RoleFilterMode>("all");
  const [viewMode, setViewMode] = React.useState<"cards" | "list">("list");
  const [inviteUserOpen, setInviteUserOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    setRoleFilter((currentRoles) =>
      currentRoles.filter((role) => roleFilterOptions.includes(role)),
    );
  }, [roleFilterOptions]);

  const openInviteUserModal = () => {
    setInviteUserOpen(true);
  };

  const closeInviteUserModal = () => {
    setInviteUserOpen(false);
  };

  const teamDictionary = useMemo(() => {
    return createTeamDictionary(teams);
  }, [teams]);

  const getTeamNames = React.useCallback(
    (user: User) => {
      if (!user.organisationIds.length) {
        return ["Guest"];
      }

      return user.organisationIds.map((teamId) => {
        return teamDictionary[teamId]?.name || "Unknown";
      });
    },
    [teamDictionary],
  );

  const visibleUsers = useMemo(() => {
    let filteredUsers = usersList;

    // if (!isCurrentUserDeveloperAdmin) {
    //   filteredUsers = filteredUsers.filter(
    //     (user) => user.userType !== UserType.DeveloperAdmin,
    //   );
    // }

    if (!canViewRestrictedUserTypes) {
      filteredUsers = filteredUsers.filter(
        (user) =>
          user.userType !== UserType.Blocked && user.userType !== UserType.Inactive,
      );
    }

    return filteredUsers;
  }, [canViewRestrictedUserTypes, isCurrentUserDeveloperAdmin, usersList]);

  const isRoleSelected = React.useCallback(
    (role: RoleChipValue) => roleFilter.includes(role),
    [roleFilter],
  );

  const toggleRoleFilter = React.useCallback((role: RoleChipValue) => {
    setRoleFilterMode("partial");
    setRoleFilter((currentRoles) => {
      if (currentRoles.includes(role)) {
        return currentRoles.filter((currentRole) => currentRole !== role);
      }

      return [...currentRoles, role];
    });
  }, []);

  const selectAllRoles = React.useCallback(() => {
    setRoleFilter(roleFilterOptions);
    setRoleFilterMode("all");
  }, [roleFilterOptions]);

  const clearRoleFilters = React.useCallback(() => {
    setRoleFilter([]);
    setRoleFilterMode("none");
  }, []);

  const filteredUsers = useMemo(() => {
    let filtered = visibleUsers;

    if (roleFilterMode === "none") {
      filtered = [];
    } else if (roleFilterMode === "partial") {
      filtered = filtered.filter((user) =>
        roleFilter.includes(getUserRoleChipValue(user)),
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((user) => {
        const teamNames = getTeamNames(user).join(" ").toLowerCase();

        return (
          user.fullName?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          teamNames.includes(query)
        );
      });
    }

    return [...filtered].sort((a, b) => {
      return (a.fullName || a.email || "")
        .toLowerCase()
        .localeCompare((b.fullName || b.email || "").toLowerCase());
    });
  }, [visibleUsers, searchQuery, roleFilter, roleFilterMode, getTeamNames]);

  return (
    <MainLayout fixedHeight>
      <Stack
        spacing={0}
        sx={{
          flex: 1,
          minHeight: 0,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "start", sm: "center" }}
          sx={{ flexShrink: 0 }}
          mb={2}
        >
          <Typography variant="h3">Users</Typography>
          {canInviteUser && (
            <Button
              variant="contained"
              onClick={openInviteUserModal}
              startIcon={<AddIcon />}
              sx={{
                borderRadius: "1.5rem",
                px: 2,
                py: 0.8,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              Invite User
            </Button>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          sx={{ flexShrink: 0 }}
        >
          <ButtonGroup
            variant="outlined"
            sx={{
              borderRadius: "0.5rem",
              "& .MuiButton-root": {
                color: "#555",
                borderColor: "#BBB",
                "&:not(:last-child)": {
                  borderRight: "1px solid #BBB",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                  color: "#999",
                },
              },
              "& .MuiButton-root:first-of-type": {
                borderTopLeftRadius: "0.5rem",
                borderBottomLeftRadius: "0.5rem",
              },
              "& .MuiButton-root:last-of-type": {
                borderTopRightRadius: "0.5rem",
                borderBottomRightRadius: "0.5rem",
              },
            }}
          >
            <Tooltip title="List view">
              <Button
                onClick={() => setViewMode("list")}
                aria-pressed={viewMode === "list"}
                sx={{ px: 1.5, py: 0.875, minWidth: 0 }}
              >
                <TableRowsIcon
                  sx={{
                    fontSize: 22,
                    color: viewMode === "list" ? "primary.main" : "#555",
                  }}
                />
              </Button>
            </Tooltip>
            <Tooltip title="Card view">
              <Button
                onClick={() => setViewMode("cards")}
                aria-pressed={viewMode === "cards"}
                sx={{ px: 1.5, py: 0.875, minWidth: 0 }}
              >
                <AppsIcon
                  sx={{
                    fontSize: 22,
                    color: viewMode === "cards" ? "primary.main" : "#555",
                  }}
                />
              </Button>
            </Tooltip>
          </ButtonGroup>

          <TextField
            size="small"
            variant="outlined"
            placeholder="Search by name, email, or team"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear user search"
                    onClick={() => setSearchQuery("")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{
              flex: 1,
              minWidth: { xs: "100%", sm: 280 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "0.5rem",
              },
            }}
          />

          <Divider
            orientation="vertical"
            flexItem
            sx={{ m: 1, height: 32, alignSelf: "center" }}
          />
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            alignItems={"center"}
          >
            <Button
              size="small"
              onClick={selectAllRoles}
              sx={{
                minWidth: 0,
                px: 0.5,
                fontSize: "0.68rem",
                fontWeight: 700,
              }}
            >
              ALL
            </Button>
            {roleFilterOptions.map((option) => {
              const selected = isRoleSelected(option);

              return (
                <RoleChip
                  key={option}
                  role={option}
                  size="small"
                  height={20}
                  fontSize="0.65rem"
                  onClick={() => toggleRoleFilter(option)}
                  sx={{
                    cursor: "pointer",
                    opacity: selected ? 1 : ROLE_CHIP_OPACITY,
                    transition:
                      "opacity 0.2s ease-in-out, transform 0.2s ease-in-out",
                    "&:hover": {
                      opacity: 1,
                      transform: "translateY(-1px)",
                    },
                  }}
                />
              );
            })}
            <Button
              size="small"
              onClick={clearRoleFilters}
              sx={{
                minWidth: 0,
                px: 0.5,
                fontSize: "0.68rem",
                fontWeight: 700,
              }}
            >
              NONE
            </Button>
          </Stack>
        </Stack>

        <Stack sx={{ flex: 1, minHeight: 0 }}>
          {isUsersLoading ? (
            <Stack
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 200,
              }}
            >
              <CircularProgress />
            </Stack>
          ) : filteredUsers.length === 0 ? (
            <Stack
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 200,
                flexDirection: "column",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                No users found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search or role filters
              </Typography>
            </Stack>
          ) : viewMode === "cards" ? (
            <UsersCardsView
              filteredUsers={filteredUsers}
              unfilteredUserCount={visibleUsers.length}
              teamDictionary={teamDictionary}
            />
          ) : (
            <UsersTableView
              filteredUsers={filteredUsers}
              unfilteredUserCount={visibleUsers.length}
              teamDictionary={teamDictionary}
            />
          )}
        </Stack>

        <InviteUserModal open={inviteUserOpen} onClose={closeInviteUserModal} />
      </Stack>
    </MainLayout>
  );
};

export default UsersPage;
